import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2, CircleCheck, CirclePause, Copy, KeyRound, ListPlus, LoaderCircle, LogIn, MoreHorizontal, Pencil, Plus, Puzzle, RefreshCw, RotateCw, ShieldCheck, ShieldX, Upload, Users } from "lucide-react";
import { api } from "../lib/api";
import { buildAccountLoginJobCreateRequest } from "../lib/accountLogin";
import { maxFreeGenerationQuotaText } from "../lib/accountQuota";
import { prepareBatchCredentials, stripInlineBatchSecrets, summarizeBatchInput, type BatchCredentialProvider } from "../lib/batchCredentials";
import { parseCookieJsonAccounts, parseCookies, type NormalizedCookie } from "../lib/cookieImport";
import { displayError, formatDate, formatNumber, truncate } from "../lib/format";
import { notify } from "../lib/notify";
import type { Account, AccountLoginBatchItem, AccountLoginJobResponse, AccountLoginJobStatus, BrowserExtensionPairing } from "../lib/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";
import { LoginCaptchaForm } from "../components/LoginCaptchaForm";

const MODEL_OPTIONS = [
  { value: "videos-standard", label: "Standard" },
  { value: "videos-fast", label: "Fast" },
  { value: "videos-mini", label: "Mini" },
  { value: "seedance-2-5", label: "Seedance 2.5" },
  { value: "videos-max", label: "Max (Academy Free)" },
  { value: "videos-8", label: "Unlimited 8s" },
] as const;
const CREDIT_MODELS = ["videos-standard", "videos-fast", "videos-mini", "seedance-2-5"] as const;
const LOGIN_JOB_STORAGE_KEY = "higgsfield2api.account-login-job";
const LOGIN_BATCH_STORAGE_KEY = "higgsfield2api.account-login-jobs";
const LOGIN_TERMINAL_STATUSES = new Set<AccountLoginJobStatus>(["SUCCEEDED", "CHALLENGE_REQUIRED", "FAILED", "EXPIRED", "CANCELLED"]);

type SupportedModel = (typeof MODEL_OPTIONS)[number]["value"];

interface CookieImportSettings {
  user_agent: string;
  proxy_url: string;
  max_concurrent_tasks: number | "";
  requests_per_second: number;
}

interface CookieImportCandidate {
  id: string;
  file_name: string;
  label: string;
  cookies: NormalizedCookie[];
}

interface AutoLoginForm {
  name: string;
  email: string;
  password: string;
  user_agent: string;
  proxy_url: string;
  max_concurrent_tasks: number | "";
  requests_per_second: number;
}

interface BatchLoginForm {
  provider: Exclude<BatchCredentialProvider, "auto"> | "";
  accounts: string;
  passwords: string;
  name: string;
  user_agent: string;
  proxy_url: string;
  max_concurrent_tasks: number | "";
  requests_per_second: number;
}

function createCookieImportSettings(): CookieImportSettings {
  return {
    user_agent: "",
    proxy_url: "",
    max_concurrent_tasks: "",
    requests_per_second: 1,
  };
}

function createAutoLoginForm(account?: Account): AutoLoginForm {
  return {
    name: account?.name || "",
    email: account?.email || "",
    password: "",
    user_agent: "",
    proxy_url: "",
    max_concurrent_tasks: "",
    requests_per_second: account?.requests_per_second || 1,
  };
}

function createBatchLoginForm(): BatchLoginForm {
  return {
    // Google is the primary paste workflow. Keeping it selected by default
    // lets a multi-line email|password block be pasted and submitted directly.
    provider: "google",
    accounts: "",
    passwords: "",
    name: "",
    user_agent: "",
    proxy_url: "",
    max_concurrent_tasks: "",
    requests_per_second: 1,
  };
}

export default function AccountsPage() {
  const { data, loading, error, refresh } = useResource((signal) => api.accounts(signal), [], 15_000);
  const [importOpen, setImportOpen] = useState(false);
  const [pairingOpen, setPairingOpen] = useState(false);
  const [pairing, setPairing] = useState<BrowserExtensionPairing | null>(null);
  const [pairingBusy, setPairingBusy] = useState(false);
  const pairingRequestVersion = useRef(0);
  const [importMode, setImportMode] = useState<"auto" | "batch" | "session">("auto");
  const [targetAccount, setTargetAccount] = useState<Account | null>(null);
  const [autoForm, setAutoForm] = useState<AutoLoginForm>(createAutoLoginForm);
  const [loginResult, setLoginResult] = useState<AccountLoginJobResponse | null>(null);
  const [loginJobId, setLoginJobId] = useState<string | null>(() => sessionStorage.getItem(LOGIN_JOB_STORAGE_KEY));
  const [loginPollingError, setLoginPollingError] = useState<string | null>(null);
  const [batchForm, setBatchForm] = useState<BatchLoginForm>(createBatchLoginForm);
  const [batchItems, setBatchItems] = useState<AccountLoginBatchItem[]>([]);
  const [batchJobIds, setBatchJobIds] = useState<string[]>(readStoredBatchJobIds);
  const [batchPollingError, setBatchPollingError] = useState<string | null>(null);
  const refreshedBatchSuccesses = useRef(new Set<string>());
  const shownCaptchaIds = useRef(new Set<string>());
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [cookieImportSettings, setCookieImportSettings] = useState<CookieImportSettings>(createCookieImportSettings);
  const [cookieImportCandidates, setCookieImportCandidates] = useState<CookieImportCandidate[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const accounts = data?.items || [];
  const accountTotal = data?.total ?? accounts.length;
  const capacityAccounts = useMemo(() => accounts.filter(isCapacityEligible), [accounts]);
  const capacity = useMemo(() => capacityAccounts.reduce((sum, item) => sum + Number(item.max_concurrent_tasks || 1), 0), [capacityAccounts]);
  const occupied = useMemo(() => accounts.reduce((sum, item) => sum + Number(item.active_count || 0), 0), [accounts]);
  const accountByEmail = useMemo(() => new Map(accounts.filter((account) => account.email).map((account) => [String(account.email).trim().toLowerCase(), account])), [accounts]);
  const batchInputSummary = useMemo(
    () => summarizeBatchInput(batchForm.accounts, batchForm.provider || "auto"),
    [batchForm.accounts, batchForm.provider],
  );
  const singleLoginInProgress = loginJobId !== null;
  const batchLoginInProgress = batchJobIds.length > 0;
  const loginInProgress = singleLoginInProgress || batchLoginInProgress;

  useEffect(() => {
    if (loginJobId || batchJobIds.length) return;
    const controller = new AbortController();
    api.activeAccountLoginJobs(controller.signal).then((result) => {
      if (controller.signal.aborted || !result.jobs.length) return;
      const ids = result.jobs.map((job) => String(job.id));
      sessionStorage.setItem(LOGIN_BATCH_STORAGE_KEY, JSON.stringify(ids));
      setBatchJobIds(ids);
      setBatchItems(result.items);
      setImportMode("batch");
    }).catch(() => { /* Existing account and login polling surfaces report connectivity. */ });
    return () => controller.abort();
    // Recover server-side browser waits when this page is opened in a new tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const waitingBatch = batchItems.find((item) => item.job.captcha?.status === "waiting" && !shownCaptchaIds.current.has(item.job.captcha.id));
    const waitingSingle = loginResult?.job.captcha?.status === "waiting" && !shownCaptchaIds.current.has(loginResult.job.captcha.id) ? loginResult.job : null;
    const waiting = waitingBatch?.job || waitingSingle;
    if (!waiting?.captcha) return;
    shownCaptchaIds.current.add(waiting.captcha.id);
    setImportMode(waitingBatch ? "batch" : "auto");
    setImportOpen(true);
    notify("Google 登录需要验证码，请输入后继续", "info");
  }, [batchItems, loginResult]);

  useEffect(() => {
    if (!loginJobId) return;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const result = await api.accountLoginJob(loginJobId, controller.signal);
        if (stopped) return;
        setLoginResult(result);
        setLoginPollingError(null);
        if (isLoginTerminal(result.job.status)) {
          sessionStorage.removeItem(LOGIN_JOB_STORAGE_KEY);
          setLoginJobId(null);
          if (result.job.status === "SUCCEEDED") await refresh();
          notify(loginCompletionMessage(result.job.status), result.job.status === "SUCCEEDED" ? "success" : result.job.status === "CHALLENGE_REQUIRED" ? "info" : "error");
          return;
        }
      } catch (caught) {
        if (!controller.signal.aborted) setLoginPollingError(displayError(caught));
      }
      if (!stopped) timer = window.setTimeout(() => void poll(), 2_000);
    };

    void poll();
    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
    // The active job id owns this polling lifecycle; refresh is safe to use from the captured render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginJobId]);

  useEffect(() => {
    if (!batchJobIds.length) return;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const result = await api.accountLoginJobsStatus(batchJobIds, controller.signal);
        if (stopped) return;
        setBatchItems((current) => mergeBatchItems(current, result.items));
        setBatchPollingError(null);
        const succeededIds = result.jobs
          .filter((job) => job.status === "SUCCEEDED")
          .map((job) => String(job.id));
        const hasNewSuccess = succeededIds.some((id) => !refreshedBatchSuccesses.current.has(id));
        succeededIds.forEach((id) => refreshedBatchSuccesses.current.add(id));
        if (hasNewSuccess) await refresh();
        if (result.jobs.length && result.jobs.every((job) => isLoginTerminal(job.status))) {
          sessionStorage.removeItem(LOGIN_BATCH_STORAGE_KEY);
          setBatchJobIds([]);
          const succeeded = result.jobs.filter((job) => job.status === "SUCCEEDED").length;
          notify(`批量登录完成：${succeeded} / ${result.jobs.length} 个成功`, succeeded === result.jobs.length ? "success" : "info");
          return;
        }
      } catch (caught) {
        if (!controller.signal.aborted) setBatchPollingError(displayError(caught));
      }
      if (!stopped) timer = window.setTimeout(() => void poll(), 2_000);
    };

    void poll();
    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
    // The persisted ID set owns this polling lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJobIds.join("|")]);

  async function submitAutoLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("auto-login");
    setLoginPollingError(null);
    try {
      const result = await api.createAccountLoginJob(
        buildAccountLoginJobCreateRequest(autoForm, targetAccount?.id),
      );
      setAutoForm((current) => ({ ...current, password: "" }));
      setLoginResult(result);
      if (isLoginTerminal(result.job.status)) {
        if (result.job.status === "SUCCEEDED") await refresh();
        notify(loginCompletionMessage(result.job.status), result.job.status === "SUCCEEDED" ? "success" : result.job.status === "CHALLENGE_REQUIRED" ? "info" : "error");
      } else {
        const id = String(result.job.id);
        sessionStorage.setItem(LOGIN_JOB_STORAGE_KEY, id);
        setLoginJobId(id);
        notify(targetAccount ? "重新登录任务已创建" : "自动登录任务已创建", "success");
      }
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setAutoForm((current) => ({ ...current, password: "" }));
      setBusy(null);
    }
  }

  async function submitBatchAutoLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!batchForm.provider) {
      notify("请先选择 Google 或 Microsoft 登录提供商", "error");
      return;
    }
    let prepared: ReturnType<typeof prepareBatchCredentials>;
    try {
      prepared = prepareBatchCredentials(batchForm.accounts, batchForm.passwords, batchForm.provider);
    } catch (caught) {
      notify(displayError(caught), "error");
      return;
    }
    const payload = {
      accounts: prepared.accounts,
      ...(prepared.passwords !== undefined ? { passwords: prepared.passwords } : {}),
      name: batchForm.name || undefined,
      user_agent: batchForm.user_agent || undefined,
      proxy_url: batchForm.proxy_url || undefined,
      ...(batchForm.max_concurrent_tasks === ""
        ? {}
        : { max_concurrent_tasks: batchForm.max_concurrent_tasks }),
      requests_per_second: batchForm.requests_per_second,
    };
    setBusy("batch-auto-login");
    setBatchPollingError(null);
    try {
      const result = await api.createAccountLoginJobsBatch(payload);
      // A successful response ends the secret-entry lifecycle, even if the
      // response is malformed and cannot be tracked below.
      setBatchForm((current) => ({ ...current, accounts: prepared.emails.join("\n"), passwords: "" }));
      const items = mergeBatchItems([], result.items);
      const ids = result.jobs.map((job) => String(job.id));
      if (!ids.length) throw new Error("批量登录接口未返回任务 ID");
      setBatchItems(items);
      refreshedBatchSuccesses.current.clear();
      const allTerminal = result.jobs.every((job) => isLoginTerminal(job.status));
      if (allTerminal) {
        const succeeded = result.jobs.filter((job) => job.status === "SUCCEEDED").length;
        if (succeeded) await refresh();
        notify(`批量登录完成：${succeeded} / ${result.jobs.length} 个成功`, succeeded === result.jobs.length ? "success" : "info");
      } else {
        sessionStorage.setItem(LOGIN_BATCH_STORAGE_KEY, JSON.stringify(ids));
        setBatchJobIds(ids);
        notify(`已创建 ${result.total || ids.length} 个自动登录任务`, "success");
      }
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  function openAccountDialog() {
    setImportMode(singleLoginInProgress ? "auto" : batchLoginInProgress ? "batch" : "auto");
    if (!loginInProgress) {
      setTargetAccount(null);
      setAutoForm(createAutoLoginForm());
      setBatchForm(createBatchLoginForm());
      setBatchItems([]);
      setBatchPollingError(null);
      setCookieImportSettings(createCookieImportSettings());
      setCookieImportCandidates([]);
      setLoginResult(null);
      setLoginPollingError(null);
    }
    setImportOpen(true);
  }

  function openRelogin(account: Account) {
    if (loginInProgress) return;
    setTargetAccount(account);
    setAutoForm(createAutoLoginForm(account));
    setLoginResult(null);
    setLoginPollingError(null);
    setImportMode("auto");
    setImportOpen(true);
  }

  function closeAccountDialog() {
    setImportOpen(false);
    setAutoForm((current) => ({ ...current, password: "" }));
    setBatchForm((current) => ({ ...current, accounts: stripInlineBatchSecrets(current.accounts), passwords: "" }));
    if (!loginInProgress) {
      setTargetAccount(null);
      setLoginResult(null);
      setLoginPollingError(null);
    }
  }

  function switchImportMode(mode: "auto" | "batch" | "session") {
    if (loginInProgress || targetAccount) return;
    setAutoForm((current) => ({ ...current, password: "" }));
    setBatchForm((current) => ({ ...current, accounts: stripInlineBatchSecrets(current.accounts), passwords: "" }));
    setImportMode(mode);
    setLoginResult(null);
    setLoginPollingError(null);
    if (mode !== "batch") {
      setBatchItems([]);
      setBatchPollingError(null);
    }
  }

  function retryAutoLogin() {
    setLoginResult(null);
    setLoginPollingError(null);
    setAutoForm((current) => ({ ...current, password: "" }));
  }

  function retryBatchLogin() {
    sessionStorage.removeItem(LOGIN_BATCH_STORAGE_KEY);
    setBatchJobIds([]);
    setBatchItems([]);
    setBatchPollingError(null);
    setBatchForm((current) => ({ ...current, accounts: stripInlineBatchSecrets(current.accounts), passwords: "" }));
  }

  function selectBatchProvider(provider: Exclude<BatchCredentialProvider, "auto">) {
    setBatchForm((current) => ({
      ...current,
      provider,
      // Never carry a pasted secret across providers: an email|password row
      // has different routing semantics in the two login flows.
      accounts: current.provider && current.provider !== provider
        ? stripInlineBatchSecrets(current.accounts)
        : current.accounts,
      passwords: current.provider && current.provider !== provider ? "" : current.passwords,
    }));
  }

  function switchToSessionImport() {
    setCookieImportSettings({
      ...createCookieImportSettings(),
      user_agent: autoForm.user_agent,
      proxy_url: autoForm.proxy_url,
      max_concurrent_tasks: autoForm.max_concurrent_tasks,
      requests_per_second: autoForm.requests_per_second,
    });
    setCookieImportCandidates([]);
    setLoginResult(null);
    setImportMode("session");
  }

  async function submitCookieImports(event: FormEvent) {
    event.preventDefault();
    if (!cookieImportCandidates.length) {
      notify("请选择一个或多个 Cookie JSON 文件", "error");
      return;
    }
    setBusy("cookie-import");
    try {
      const completed: Array<{ index: number; item: AccountLoginBatchItem }> = [];
      const failures: string[] = [];
      let cursor = 0;
      const worker = async () => {
        while (cursor < cookieImportCandidates.length) {
          const index = cursor++;
          const candidate = cookieImportCandidates[index];
          try {
            const result = await api.createAccountCookieSessionImport({
              cookies: candidate.cookies,
              user_agent: cookieImportSettings.user_agent || undefined,
              proxy_url: cookieImportSettings.proxy_url || undefined,
              ...(cookieImportSettings.max_concurrent_tasks === ""
                ? {}
                : { max_concurrent_tasks: cookieImportSettings.max_concurrent_tasks }),
              requests_per_second: cookieImportSettings.requests_per_second,
            });
            completed.push({ index, item: { email: candidate.label, job: result.job } });
          } catch (caught) {
            failures.push(`${candidate.label}：${displayError(caught)}`);
          }
        }
      };
      await Promise.all(Array.from(
        { length: Math.min(6, cookieImportCandidates.length) },
        () => worker(),
      ));

      const items = completed.sort((left, right) => left.index - right.index).map(({ item }) => item);
      const ids = items.map((item) => String(item.job.id));
      if (!ids.length) throw new Error(failures[0] || "未能创建 Cookie 导入任务");
      setBatchItems(items);
      refreshedBatchSuccesses.current.clear();
      setBatchPollingError(failures.length ? `有 ${failures.length} 个文件未创建任务：${failures.slice(0, 3).join("；")}` : null);
      setCookieImportCandidates([]);
      setCookieImportSettings(createCookieImportSettings());
      setLoginResult(null);
      setImportMode("batch");
      sessionStorage.setItem(LOGIN_BATCH_STORAGE_KEY, JSON.stringify(ids));
      setBatchJobIds(ids);
      notify(`已创建 ${ids.length} 个导入任务${failures.length ? `，${failures.length} 个文件失败` : ""}，将逐个自动识别并配置 MCP`, failures.length ? "info" : "success");
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function loadCookieJsonFiles(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const files = Array.from(input.files || []);
    input.value = "";
    if (!files.length) return;
    try {
      const existingSessions = new Set(cookieImportCandidates.map((candidate) => cookieSessionValue(candidate.cookies)));
      const selectedSessions = new Set<string>();
      const loaded: CookieImportCandidate[] = [];
      const errors: string[] = [];
      let duplicateCount = 0;
      for (const file of files) {
        try {
          if (file.size > 512 * 1024) throw new Error("文件超过 512 KB");
          const accounts = parseCookieJsonAccounts(await file.text());
          const baseName = file.name.replace(/\.json$/i, "");
          for (const [index, account] of accounts.entries()) {
            const session = cookieSessionValue(account.cookies);
            if (existingSessions.has(session) || selectedSessions.has(session)) {
              duplicateCount += 1;
              continue;
            }
            selectedSessions.add(session);
            loaded.push({
              id: `${file.name}:${file.lastModified}:${index}:${crypto.randomUUID()}`,
              file_name: file.name,
              label: account.label || (accounts.length > 1 ? `${baseName} #${index + 1}` : baseName),
              cookies: account.cookies,
            });
          }
        } catch (caught) {
          errors.push(`${file.name}：${displayError(caught)}`);
        }
      }
      if (cookieImportCandidates.length + loaded.length > 100) throw new Error("每批最多导入 100 个账号会话");
      if (loaded.length) setCookieImportCandidates((current) => [...current, ...loaded]);
      if (!loaded.length && errors.length) throw new Error(errors.slice(0, 3).join("；"));
      notify(
        `已自动识别 ${loaded.length} 个账号会话${duplicateCount ? `，跳过 ${duplicateCount} 个重复会话` : ""}${errors.length ? `，${errors.length} 个文件格式无效` : ""}`,
        errors.length ? "info" : "success",
      );
    } catch (caught) {
      notify(displayError(caught), "error");
    }
  }

  async function issueBrowserPairing() {
    const requestVersion = ++pairingRequestVersion.current;
    setPairing(null);
    setPairingBusy(true);
    try {
      const result = await api.createBrowserExtensionPairing();
      if (requestVersion !== pairingRequestVersion.current) {
        await api.revokeBrowserExtensionPairings();
        return;
      }
      setPairing(result);
      notify("一次性配对码已生成", "success");
    } catch (caught) {
      if (requestVersion === pairingRequestVersion.current) {
        notify(displayError(caught), "error");
      }
    } finally {
      setPairingBusy(false);
    }
  }

  function openBrowserPairing() {
    setPairing(null);
    setPairingOpen(true);
    void issueBrowserPairing();
  }

  function closeBrowserPairing() {
    pairingRequestVersion.current += 1;
    setPairingOpen(false);
    setPairing(null);
    void api.revokeBrowserExtensionPairings().catch(() => {
      notify("配对码撤销失败，将在到期后自动失效", "info");
    });
  }

  async function copyPairingCode() {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.pairing_code);
      notify("配对码已复制", "success");
    } catch {
      notify("无法访问剪贴板，请手动选择配对码", "error");
    }
  }

  async function validate(account: Account) {
    setBusy(`validate-${account.id}`);
    try {
      await api.validateAccount(account.id);
      notify(`${account.name || account.email || account.id} 已排队等待协议验证`, "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function toggle(account: Account) {
    const next = !isEnabled(account);
    setBusy(`status-${account.id}`);
    try {
      await api.setAccountStatus(account.id, next);
      notify(next ? "账号已启用" : "账号已停用", "success");
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  async function saveAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editAccount) return;
    const values = new FormData(event.currentTarget);
    const replacementToken = String(values.get("token") || "").trim();
    const replacementClerkCookie = String(values.get("clerk_cookie") || "").trim();
    const replacementUserAgent = String(values.get("user_agent") || "").trim();
    const replacementCookies = String(values.get("cookies") || "").trim();
    const replacementProxy = String(values.get("proxy_url") || "").trim();
    setBusy(`edit-${editAccount.id}`);
    try {
      const result = await api.updateAccount(editAccount.id, {
        name: values.get("name"),
        max_concurrent_tasks: Number(values.get("max_concurrent_tasks")),
        requests_per_second: Number(values.get("requests_per_second")),
        ...(replacementToken ? { token: replacementToken } : {}),
        ...(replacementClerkCookie ? { clerk_cookie: replacementClerkCookie } : {}),
        ...(replacementUserAgent ? { user_agent: replacementUserAgent } : {}),
        ...(replacementCookies ? { cookies: parseCookies(replacementCookies) } : {}),
        ...(replacementProxy ? { proxy_url: replacementProxy } : {}),
      });
      notify(result.validation_pending ? "凭据已替换，等待协议 worker 验证" : "账号设置已保存", "success");
      closeEdit();
      await refresh();
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setBusy(null);
    }
  }

  function openEdit(account: Account) {
    setEditAccount(account);
  }

  function closeEdit() {
    setEditAccount(null);
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="账号与容量"
        description={`${accountTotal} 个账号，${occupied} / ${capacity} 个槽位占用`}
        actions={
          <>
            <button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button>
            <button className="button button-secondary" onClick={openBrowserPairing} disabled={busy !== null || pairingBusy}><Puzzle size={16} />浏览器导入</button>
            <button className="button button-primary" onClick={openAccountDialog}>
              {loginInProgress ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />}
              {loginInProgress ? "查看登录状态" : "添加账号"}
            </button>
          </>
        }
      />

      <section className="capacity-strip">
        <div><Users size={17} /><span>可用账号</span><strong>{formatNumber(capacityAccounts.length)}</strong></div>
        <div><CircleCheck size={17} /><span>配置槽位</span><strong>{formatNumber(capacity)}</strong></div>
        <div><MoreHorizontal size={17} /><span>在途任务</span><strong>{formatNumber(occupied)}</strong></div>
        <div><CirclePause size={17} /><span>不可用</span><strong>{formatNumber(accounts.length - capacityAccounts.length)}</strong></div>
      </section>

      <section className="panel panel-flush">
        {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : !accounts.length ? <EmptyState label="还没有导入 Higgsfield 账号" /> : (
          <div className="table-wrap">
            <table className="accounts-table">
              <thead><tr><th>账号</th><th>状态</th><th>槽位</th><th>套餐 / 余额</th><th>代理</th><th>会话</th><th className="actions-cell">操作</th></tr></thead>
              <tbody>{accounts.map((account) => {
                const enabled = isEnabled(account);
                const accountStatus = account.status || (enabled ? "ACTIVE" : account.disabled_reason ? "REAUTH_REQUIRED" : "DISABLED");
                return (
                  <tr key={account.id}>
                    <td><div className="primary-cell"><strong>{account.name || account.email || `Account ${account.id}`}</strong><span>{account.email || account.remark || `ID ${account.id}`}</span><small title={normalizeSupportedModels(account.supported_models).join(", ")}>{accountModelPolicy(account)}：{modelSummary(account.supported_models)}</small></div></td>
                    <td><StatusBadge value={accountStatus} />{account.disabled_reason && <span className="cell-note" title={account.disabled_reason}>{truncate(account.disabled_reason, 34)}</span>}</td>
                    <td><div className="slot-count"><strong>{account.active_count || 0}</strong><span>/ {account.max_concurrent_tasks || 1}</span>{isFuture(account.create_cooldown_until) && <small title="上游并发已满，暂停向此账号创建新任务">创建冷却至 {formatDate(account.create_cooldown_until)}</small>}</div></td>
                    <td><div className="primary-cell"><strong>{accountPlanName(account)}</strong><span>{account.credits === null || account.credits === undefined ? "余额 -" : `余额 ${formatNumber(account.credits)}`}</span>{maxFreeGenerationQuotaText(account) && <small>{maxFreeGenerationQuotaText(account)}</small>}</div></td>
                    <td title={account.proxy_url || (account.has_proxy ? "已配置代理" : "直连")}>{account.proxy_url ? truncate(account.proxy_url, 24) : account.has_proxy ? "已配置" : <span className="muted">直连</span>}</td>
                    <td><div className="primary-cell"><span>{formatDate(account.last_validated_at)}</span><small>到期 {formatDate(account.session_expires_at)}</small></div></td>
                    <td className="actions-cell">
                      <button className="icon-button" title="自动重新登录" aria-label="自动重新登录" disabled={busy !== null || loginInProgress} onClick={() => openRelogin(account)}><LogIn size={17} /></button>
                      <button className="icon-button" title="验证账号" aria-label="验证账号" disabled={busy !== null} onClick={() => void validate(account)}><ShieldCheck size={17} /></button>
                      <button className="icon-button" title="编辑账号" aria-label="编辑账号" disabled={busy !== null} onClick={() => openEdit(account)}><Pencil size={17} /></button>
                      <button className={`icon-button ${enabled ? "icon-button-danger" : "icon-button-success"}`} title={enabled ? "停用账号" : "启用账号"} aria-label={enabled ? "停用账号" : "启用账号"} disabled={busy !== null} onClick={() => void toggle(account)}>{enabled ? <CirclePause size={17} /> : <CircleCheck size={17} />}</button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={importOpen}
        onClose={closeAccountDialog}
        title={targetAccount ? "重新登录账号" : importMode === "batch" ? "批量账号任务" : importMode === "session" ? "批量导入 Cookie" : "添加账号"}
        description={targetAccount ? `更新 ${targetAccount.name || targetAccount.email || targetAccount.id} 的加密会话。` : importMode === "batch" ? "批量任务独立执行，页面关闭后仍会继续。" : importMode === "session" ? "只需选择文件，账号身份、套餐、额度、并发和 MCP 凭证均自动识别。" : "登录会话由服务端加密保存，管理端不会接收真实 Token 或 Cookie。"}
        width="large"
      >
        {!targetAccount && !loginInProgress && (
          <div className="segmented-control segmented-control-three" role="tablist" aria-label="账号添加方式">
            <button type="button" role="tab" aria-selected={importMode === "auto"} className={importMode === "auto" ? "active" : ""} onClick={() => switchImportMode("auto")}><KeyRound size={15} />自动登录</button>
            <button type="button" role="tab" aria-selected={importMode === "batch"} className={importMode === "batch" ? "active" : ""} onClick={() => switchImportMode("batch")}><ListPlus size={15} />批量登录</button>
            <button type="button" role="tab" aria-selected={importMode === "session"} className={importMode === "session" ? "active" : ""} onClick={() => switchImportMode("session")}><Upload size={15} />会话导入</button>
          </div>
        )}

        {importMode === "auto" ? (
          loginResult || loginInProgress ? (
            <LoginJobPanel
              result={loginResult}
              pollingError={loginPollingError}
              canImportSession={!targetAccount}
              onRetry={retryAutoLogin}
              onImportSession={switchToSessionImport}
              onClose={closeAccountDialog}
            />
          ) : (
            <form onSubmit={submitAutoLogin} className="form-stack auto-login-form">
              <div className="form-grid">
                <label className="field"><span>名称</span><input value={autoForm.name} onChange={(event) => setAutoForm({ ...autoForm, name: event.target.value })} placeholder="Production 01" /></label>
                <label className="field"><span>Microsoft 邮箱</span><input type="email" autoComplete="username" value={autoForm.email} onChange={(event) => setAutoForm({ ...autoForm, email: event.target.value })} required /></label>
              </div>
              <div className="form-grid">
                <label className="field"><span>Microsoft 密码</span><input type="password" autoComplete="current-password" value={autoForm.password} onChange={(event) => setAutoForm({ ...autoForm, password: event.target.value })} required /></label>
                <label className="field"><span>{targetAccount?.has_proxy ? "代理 URL（留空保持）" : "代理 URL"}</span><input value={autoForm.proxy_url} onChange={(event) => setAutoForm({ ...autoForm, proxy_url: event.target.value })} placeholder={targetAccount?.has_proxy ? "已配置代理" : "http://user:pass@host:port"} spellCheck={false} /></label>
              </div>
              <label className="field"><span>{targetAccount ? "User-Agent（留空保持）" : "User-Agent"}</span><input value={autoForm.user_agent} onChange={(event) => setAutoForm({ ...autoForm, user_agent: event.target.value })} placeholder="Mozilla/5.0 ..." minLength={8} maxLength={1024} spellCheck={false} /></label>
              <div className="form-grid">
                <label className="field"><span>并发槽位</span><input type="number" min={1} max={400} value={autoForm.max_concurrent_tasks} placeholder="自动" onChange={(event) => setAutoForm({ ...autoForm, max_concurrent_tasks: event.target.value === "" ? "" : Number(event.target.value) })} /></label>
                <label className="field"><span>请求速率 / 秒</span><input type="number" min={0.1} max={20} step={0.1} value={autoForm.requests_per_second} onChange={(event) => setAutoForm({ ...autoForm, requests_per_second: Number(event.target.value) })} required /></label>
              </div>
              <p className="batch-input-hint">登录后自动识别账号类型：Free 仅启用 Max；会员积分账号启用 Standard、Fast、Mini。</p>
              <div className="modal-actions"><button type="button" className="button button-secondary" onClick={closeAccountDialog}>取消</button><button type="submit" className="button button-primary" disabled={busy === "auto-login"}>{busy === "auto-login" ? <LoaderCircle className="spin" size={16} /> : <LogIn size={16} />}{targetAccount ? "重新登录" : "登录并导入"}</button></div>
            </form>
          )
        ) : importMode === "batch" ? (
          batchItems.length || batchLoginInProgress ? (
            <BatchLoginPanel
              items={batchItems}
              pollingError={batchPollingError}
              accounts={accountByEmail}
              running={batchLoginInProgress}
              onRetry={retryBatchLogin}
              onClose={closeAccountDialog}
            />
          ) : (
            <form onSubmit={submitBatchAutoLogin} className="form-stack batch-login-form">
              <div className="batch-provider-picker">
                <span className="field-label">登录提供商（必选）</span>
                <div className="segmented-control batch-provider-control" role="radiogroup" aria-label="批量登录提供商">
                  <button type="button" role="radio" aria-checked={batchForm.provider === "google"} className={batchForm.provider === "google" ? "active" : ""} onClick={() => selectBatchProvider("google")}>Google</button>
                  <button type="button" role="radio" aria-checked={batchForm.provider === "microsoft"} className={batchForm.provider === "microsoft" ? "active" : ""} onClick={() => selectBatchProvider("microsoft")}>Microsoft</button>
                </div>
                <p className="batch-input-hint">
                  {batchForm.provider === "google"
                    ? "已选择 Google：支持 email|password、email---password---恢复邮箱，以及带 Higgsfield Cookie 的六段导出；带 Cookie 的账号优先恢复会话。"
                    : batchForm.provider === "microsoft"
                    ? "已选择 Microsoft：支持 email|主密码|备用密码、四段 Pipe、Tab 和兼容的邮箱密码格式。"
                    : "请先选择账号实际使用的登录提供商。Google 邮箱密码请选择 Google。"}
                </p>
              </div>
              <div className="batch-credential-input">
                <label className="field">
                  <span>{batchForm.provider === "google" ? "Google 账号凭据（每行 email|password）" : batchForm.provider === "microsoft" ? "Microsoft 账号凭据" : "账号凭据"}</span>
                  <textarea
                    className="batch-accounts-input"
                    rows={12}
                    value={batchForm.accounts}
                    onChange={(event) => setBatchForm({ ...batchForm, accounts: event.target.value })}
                    placeholder={batchForm.provider === "google"
                      ? "直接粘贴多行：\nuser.one@example.com|password\n也支持带 Cookie 的六段 Google 导出格式"
                      : batchForm.provider === "microsoft"
                      ? "直接粘贴多行 Microsoft 凭据：\nuser@example.com|主密码|备用密码\n也支持 email|password、四段刷新凭据、Tab、email,password、email---password"
                      : "选择 Google 或 Microsoft 后粘贴账号凭据"}
                    autoComplete="off"
                    disabled={!batchForm.provider}
                    required
                    spellCheck={false}
                  />
                </label>
                <p className="batch-input-hint">{batchInputSummary || (batchForm.provider ? "每行一条账号凭据。" : "选择登录提供商后显示对应格式。")}</p>
              </div>
              {batchForm.provider === "microsoft" && <details className="batch-legacy-passwords">
                <summary>兼容旧格式：只粘贴邮箱时填写密码（可选）</summary>
                <label className="field">
                  <span>统一密码或逐行密码</span>
                  <textarea rows={3} value={batchForm.passwords} onChange={(event) => setBatchForm({ ...batchForm, passwords: event.target.value })} autoComplete="new-password" spellCheck={false} />
                </label>
                <p className="batch-input-hint">已包含密码的整段凭据不需要填写这里；留空即可。</p>
              </details>}
              <label className="field"><span>统一名称</span><input value={batchForm.name} onChange={(event) => setBatchForm({ ...batchForm, name: event.target.value })} placeholder="Production" /></label>
              <div className="form-grid">
                <label className="field"><span>User-Agent</span><input value={batchForm.user_agent} onChange={(event) => setBatchForm({ ...batchForm, user_agent: event.target.value })} placeholder="Mozilla/5.0 ..." spellCheck={false} /></label>
                <label className="field"><span>代理 URL</span><input value={batchForm.proxy_url} onChange={(event) => setBatchForm({ ...batchForm, proxy_url: event.target.value })} placeholder="http://user:pass@host:port" spellCheck={false} /></label>
              </div>
              <div className="form-grid batch-settings-grid">
                <label className="field"><span>并发槽位</span><input type="number" min={1} max={400} value={batchForm.max_concurrent_tasks} placeholder="自动" onChange={(event) => setBatchForm({ ...batchForm, max_concurrent_tasks: event.target.value === "" ? "" : Number(event.target.value) })} /></label>
                <label className="field"><span>请求速率 / 秒</span><input type="number" min={0.1} max={20} step={0.1} value={batchForm.requests_per_second} onChange={(event) => setBatchForm({ ...batchForm, requests_per_second: Number(event.target.value) })} required /></label>
              </div>
              <p className="batch-input-hint">登录后逐个自动识别账号类型：Free 仅启用 Max；会员积分账号启用 Standard、Fast、Mini。</p>
              <div className="modal-actions"><button type="button" className="button button-secondary" onClick={closeAccountDialog}>取消</button><button type="submit" className="button button-primary" disabled={busy === "batch-auto-login" || !batchForm.provider}>{busy === "batch-auto-login" ? <LoaderCircle className="spin" size={16} /> : <ListPlus size={16} />}{batchForm.provider === "google" ? "创建 Google 登录任务" : batchForm.provider === "microsoft" ? "创建 Microsoft 登录任务" : "创建登录任务"}</button></div>
            </form>
          )
        ) : (
          <form onSubmit={submitCookieImports} className="form-stack cookie-import-form">
            <label className="cookie-import-picker">
              <Upload size={22} />
              <strong>选择一个或多个 Cookie JSON 文件</strong>
              <span>支持一次多选，也可以分多次继续添加；每批最多 100 个账号</span>
              <input type="file" accept=".json,application/json" multiple onChange={(event) => void loadCookieJsonFiles(event)} />
            </label>
            {cookieImportCandidates.length ? (
              <div className="cookie-import-selection">
                <div className="cookie-import-selection-heading">
                  <div><CircleCheck size={17} /><strong>已自动识别 {cookieImportCandidates.length} 个账号会话</strong></div>
                  <button type="button" className="button button-secondary" onClick={() => setCookieImportCandidates([])}>清空</button>
                </div>
                <div className="cookie-import-list">
                  {cookieImportCandidates.map((candidate) => (
                    <div className="cookie-import-row" key={candidate.id}>
                      <div className="primary-cell"><strong>{candidate.label}</strong><span>{candidate.file_name} · {candidate.cookies.length} 条 Cookie</span></div>
                      <button type="button" className="button button-secondary" onClick={() => setCookieImportCandidates((current) => current.filter((item) => item.id !== candidate.id))}>移除</button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="callout"><CircleCheck size={16} /><span>无需填写邮箱、名称、Token、Cookie 文本、并发或模型；系统会按每个文件逐个识别并自动配置 MCP。</span></div>
            )}
            <details className="batch-legacy-passwords cookie-import-advanced">
              <summary>高级设置（可选，默认无需填写）</summary>
              <div className="form-grid">
                <label className="field"><span>User-Agent</span><input value={cookieImportSettings.user_agent} onChange={(event) => setCookieImportSettings({ ...cookieImportSettings, user_agent: event.target.value })} placeholder="自动" spellCheck={false} /></label>
                <label className="field"><span>统一代理 URL</span><input value={cookieImportSettings.proxy_url} onChange={(event) => setCookieImportSettings({ ...cookieImportSettings, proxy_url: event.target.value })} placeholder="直连" spellCheck={false} /></label>
              </div>
              <div className="form-grid">
                <label className="field"><span>并发槽位</span><input type="number" min={1} max={400} value={cookieImportSettings.max_concurrent_tasks} placeholder="自动识别" onChange={(event) => setCookieImportSettings({ ...cookieImportSettings, max_concurrent_tasks: event.target.value === "" ? "" : Number(event.target.value) })} /></label>
                <label className="field"><span>请求速率 / 秒</span><input type="number" min={0.1} max={20} step={0.1} value={cookieImportSettings.requests_per_second} onChange={(event) => setCookieImportSettings({ ...cookieImportSettings, requests_per_second: Number(event.target.value) })} /></label>
              </div>
            </details>
            <p className="batch-input-hint">兼容单账号 Cookie 数组、多个 JSON 文件，以及包含 accounts / sessions / profiles 数组的多账号 JSON 包；重复会话会自动跳过。</p>
            <div className="modal-actions"><button type="button" className="button button-secondary" onClick={closeAccountDialog}>取消</button><button type="submit" className="button button-primary" disabled={busy === "cookie-import" || !cookieImportCandidates.length}>{busy === "cookie-import" ? <LoaderCircle className="spin" size={16} /> : <Upload size={16} />}批量导入并配置 MCP</button></div>
          </form>
        )}
      </Modal>

      <Modal
        open={pairingOpen}
        onClose={closeBrowserPairing}
        title="浏览器账号导入"
        description="配对码仅可使用一次，10 分钟后自动失效。"
        width="medium"
      >
        <div className="form-stack">
          <label className="field">
            <span>一次性配对码</span>
            <div className="pairing-code-row">
              <code>{pairing?.pairing_code || (pairingBusy ? "正在生成..." : "生成失败")}</code>
              <button type="button" className="icon-button" title="复制配对码" aria-label="复制配对码" disabled={!pairing} onClick={() => void copyPairingCode()}><Copy size={16} /></button>
            </div>
          </label>
          {pairing && <p className="pairing-expiry">有效期至 {formatDate(pairing.expires_at)}</p>}
          <div className="modal-actions">
            <button type="button" className="button button-secondary" onClick={closeBrowserPairing}><ShieldX size={16} />撤销并关闭</button>
            <button type="button" className="button button-secondary" disabled={pairingBusy} onClick={() => void issueBrowserPairing()}>{pairingBusy ? <LoaderCircle className="spin" size={16} /> : <RotateCw size={16} />}重新生成</button>
            <button type="button" className="button button-primary" disabled={!pairing} onClick={() => void copyPairingCode()}><Copy size={16} />复制</button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(editAccount)} onClose={closeEdit} title="编辑账号" description="替换凭据字段留空时保持原值。" width="medium">
        {editAccount && <form onSubmit={saveAccount} className="form-stack">
          <label className="field"><span>名称</span><input name="name" defaultValue={editAccount.name || ""} /></label>
          <div className="form-grid"><label className="field"><span>并发槽位</span><input name="max_concurrent_tasks" type="number" min={1} max={400} defaultValue={editAccount.max_concurrent_tasks || 1} required /></label><label className="field"><span>请求速率 / 秒</span><input name="requests_per_second" type="number" min={0.1} max={20} step={0.1} defaultValue={editAccount.requests_per_second || 1} required /></label></div>
          <label className="field"><span>替换代理 URL（留空保持）</span><input name="proxy_url" spellCheck={false} /></label>
          <p className="batch-input-hint">模型由账号套餐和积分自动识别，重新验证后会自动更新。</p>
          <label className="field"><span>替换 Bearer Token（留空保持）</span><textarea name="token" rows={2} minLength={8} spellCheck={false} /></label>
          <label className="field"><span>替换 Clerk 会话 Cookie（留空保持）</span><textarea name="clerk_cookie" rows={3} minLength={8} spellCheck={false} /></label>
          <label className="field"><span>替换 User-Agent（留空保持）</span><input name="user_agent" minLength={8} spellCheck={false} /></label>
          <label className="field"><span>替换 Cookies（留空保持）</span><textarea name="cookies" rows={3} placeholder={'JSON 数组或 name=value; name2=value2'} spellCheck={false} /></label>
          <div className="modal-actions"><button type="button" className="button button-secondary" onClick={closeEdit}>取消</button><button className="button button-primary" type="submit" disabled={busy !== null}>保存</button></div>
        </form>}
      </Modal>
    </div>
  );
}

function LoginJobPanel({
  result,
  pollingError,
  canImportSession,
  onRetry,
  onImportSession,
  onClose,
}: {
  result: AccountLoginJobResponse | null;
  pollingError: string | null;
  canImportSession: boolean;
  onRetry: () => void;
  onImportSession: () => void;
  onClose: () => void;
}) {
  const job = result?.job;
  const status = job?.status;
  const terminal = isLoginTerminal(status);
  const succeeded = status === "SUCCEEDED";
  const account = result?.account;

  return (
    <div className="login-job-panel">
      <div className="login-job-heading">
        <span className={`login-job-icon ${succeeded ? "login-job-icon-success" : terminal ? "login-job-icon-danger" : "login-job-icon-running"}`}>
          {succeeded ? <CheckCircle2 size={20} /> : terminal ? <AlertTriangle size={20} /> : <LoaderCircle className="spin" size={20} />}
        </span>
        <div>
          <div className="login-job-title">
            <strong>{status ? loginStatusTitle(status) : "正在读取登录任务"}</strong>
            {status && <StatusBadge value={status} />}
          </div>
          <p>{status ? loginStatusDescription(status) : "正在获取最新状态。"}</p>
        </div>
      </div>

      {job && (
        <div className="login-job-meta">
          <span>任务 <code>{job.id}</code></span>
          {job.email && <span>{job.email}</span>}
          {job.attempts !== undefined && <span>尝试 {job.attempts} / {job.max_attempts || "-"}</span>}
          <span>创建 {formatDate(job.created_at)}</span>
          {job.next_attempt_at && <span>下次重试 {formatDate(job.next_attempt_at)}</span>}
          {job.completed_at && <span>完成 {formatDate(job.completed_at)}</span>}
        </div>
      )}

      {pollingError && <div className="callout callout-warning"><AlertTriangle size={15} /><span>状态刷新失败，系统将继续重试：{pollingError}</span></div>}
      {job?.captcha && <LoginCaptchaForm key={job.captcha.id} job={job} />}
      {terminal && !succeeded && (job?.error_code || job?.error_message) && (
        <div className="form-error">{displayError(job?.error_message || "登录任务未完成", job?.error_code)}</div>
      )}

      {succeeded && account && (
        <div className="login-result">
          <div className="login-result-account">
            <span>已导入账号</span>
            <strong>{account.name || account.email || `Account ${account.id}`}</strong>
            <small>{account.email || `ID ${account.id}`} · {accountPlanName(account)} · {account.credits === null || account.credits === undefined ? "余额 -" : `余额 ${formatNumber(account.credits)}`}</small>
          </div>
          <div className="credential-result-grid" aria-label="已保存的会话信息">
            <CredentialResult label="Bearer Token" configured={account.has_token} />
            <CredentialResult label="Clerk 会话" configured={account.has_clerk_session} />
            <CredentialResult label="Cookies" configured={account.has_cookies} />
            <CredentialResult label="DataDome" configured={account.has_datadome} optional />
            <CredentialResult label="代理" configured={account.has_proxy} optional />
          </div>
        </div>
      )}

      <div className="modal-actions">
        {terminal && !succeeded && <button type="button" className="button button-secondary" onClick={onRetry}><RefreshCw size={16} />重试登录</button>}
        {terminal && !succeeded && canImportSession && <button type="button" className="button button-secondary" onClick={onImportSession}><Upload size={16} />改用会话导入</button>}
        <button type="button" className="button button-primary" onClick={onClose}>{terminal ? "完成" : "后台运行"}</button>
      </div>
    </div>
  );
}

function BatchLoginPanel({
  items,
  pollingError,
  accounts,
  running,
  onRetry,
  onClose,
}: {
  items: AccountLoginBatchItem[];
  pollingError: string | null;
  accounts: Map<string, Account>;
  running: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  const counts = batchStatusCounts(items);
  const finished = counts.succeeded + counts.failed + counts.challenge + counts.cancelled;
  const terminal = Boolean(items.length) && !running && finished === items.length;

  return (
    <div className="login-job-panel batch-login-panel">
      <div className="login-job-heading">
        <span className={`login-job-icon ${terminal && counts.failed + counts.challenge === 0 ? "login-job-icon-success" : terminal ? "login-job-icon-danger" : "login-job-icon-running"}`}>
          {terminal && counts.failed + counts.challenge === 0 ? <CheckCircle2 size={20} /> : terminal ? <AlertTriangle size={20} /> : <LoaderCircle className="spin" size={20} />}
        </span>
        <div>
          <div className="login-job-title"><strong>{terminal ? "批量登录已完成" : "批量登录执行中"}</strong>{running && <StatusBadge value="RUNNING" />}</div>
          <p>{items.length ? `${finished} / ${items.length} 个任务已完成。` : "正在读取批量任务状态。"}</p>
        </div>
      </div>

      <div className="batch-login-summary" aria-label="批量登录汇总">
        <div><span>总数</span><strong>{items.length || "-"}</strong></div>
        <div><span>执行中</span><strong>{counts.active}</strong></div>
        <div><span>成功</span><strong>{counts.succeeded}</strong></div>
        <div><span>需验证</span><strong>{counts.challenge + items.filter((item) => Boolean(item.job.captcha)).length}</strong></div>
        <div><span>失败</span><strong>{counts.failed + counts.cancelled}</strong></div>
      </div>

      {pollingError && <div className="callout callout-warning"><AlertTriangle size={15} /><span>状态刷新失败，系统将继续重试：{pollingError}</span></div>}
      <div className="batch-login-results" role="list" aria-label="逐账号登录状态">
        {items.map((item) => {
          const job = item.job;
          const email = item.email || job.email || "";
          const account = item.account || accounts.get(email.trim().toLowerCase());
          const rawError = job.error_message || job.error_code;
          const error = rawError ? displayError(job.error_message || rawError, job.error_code) : null;
          return (
            <div className="batch-login-row" role="listitem" key={String(job.id)}>
              <div className="primary-cell">
                <strong>{email || `Account job ${job.id}`}</strong>
                <span>任务 {job.id}</span>
              </div>
              <StatusBadge value={job.status} />
              <div className="batch-login-outcome">
                {job.status === "SUCCEEDED" && account ? (
                  <>
                    <strong>{accountPlanName(account)}</strong>
                    <span>{account.credits === null || account.credits === undefined ? "余额 -" : `余额 ${formatNumber(account.credits)}`}{accountEntitlementSummary(account) ? ` · ${accountEntitlementSummary(account)}` : ""}</span>
                  </>
                ) : error ? (
                  <span className="batch-login-error" title={error}>{truncate(error, 86)}</span>
                ) : (
                  <span>{job.attempts !== undefined ? `尝试 ${job.attempts} / ${job.max_attempts || "-"}` : loginStatusDescription(job.status)}</span>
                )}
              </div>
              {job.captcha && <LoginCaptchaForm key={job.captcha.id} job={job} />}
            </div>
          );
        })}
        {!items.length && <div className="batch-login-loading"><LoaderCircle className="spin" size={18} /><span>正在加载任务</span></div>}
      </div>

      <div className="modal-actions">
        {terminal && <button type="button" className="button button-secondary" onClick={onRetry}><RefreshCw size={16} />新建批次</button>}
        <button type="button" className="button button-primary" onClick={onClose}>{terminal ? "完成" : "后台运行"}</button>
      </div>
    </div>
  );
}

function CredentialResult({ label, configured, optional = false }: { label: string; configured?: boolean; optional?: boolean }) {
  return (
    <div className={configured ? "credential-result credential-result-ok" : "credential-result"}>
      {configured ? <CircleCheck size={15} /> : <CirclePause size={15} />}
      <span>{label}</span>
      <small>{configured ? "已保存" : optional ? "未配置" : "未返回"}</small>
    </div>
  );
}

function isLoginTerminal(status?: AccountLoginJobStatus): boolean {
  return Boolean(status && LOGIN_TERMINAL_STATUSES.has(status));
}

function loginStatusTitle(status: AccountLoginJobStatus): string {
  const titles: Record<AccountLoginJobStatus, string> = {
    QUEUED: "登录任务已排队",
    RUNNING: "正在自动登录",
    RETRY_WAIT: "等待重试登录",
    SUCCEEDED: "账号登录成功",
    CHALLENGE_REQUIRED: "需要额外验证",
    FAILED: "自动登录失败",
    EXPIRED: "登录任务已过期",
    CANCELLED: "登录任务已取消",
  };
  return titles[status];
}

function loginStatusDescription(status: AccountLoginJobStatus): string {
  const descriptions: Record<AccountLoginJobStatus, string> = {
    QUEUED: "等待登录 worker 领取任务。",
    RUNNING: "正在建立会话并验证账号。",
    RETRY_WAIT: "当前尝试未完成，将按计划自动重试。",
    SUCCEEDED: "会话信息已加密保存，账号可以进入验证流程。",
    CHALLENGE_REQUIRED: "登录服务要求完成验证码、多因素认证或通行密钥验证。",
    FAILED: "登录未能建立有效会话，请检查错误后重试。",
    EXPIRED: "任务未在有效期内完成，请重新提交。",
    CANCELLED: "任务已停止，可以重新提交登录。",
  };
  return descriptions[status];
}

function loginCompletionMessage(status: AccountLoginJobStatus): string {
  if (status === "SUCCEEDED") return "账号登录成功，会话已加密保存";
  if (status === "CHALLENGE_REQUIRED") return "自动登录需要额外验证";
  if (status === "EXPIRED") return "自动登录任务已过期";
  if (status === "CANCELLED") return "自动登录任务已取消";
  return "自动登录失败";
}

function normalizeSupportedModels(values?: string[]): SupportedModel[] {
  const canonical = new Set((values || []).map(canonicalModel));
  const selected = MODEL_OPTIONS.map((option) => option.value).filter((value) => canonical.has(value));
  return selected;
}

function canonicalModel(value: string): SupportedModel | "" {
  const normalized = value.trim().toLowerCase();
  if (["videos-standard", "tvideos", "seedance-2.0", "seedance-2", "seedance-2-0", "seedance_2_0", "seedance-2.0-standard", "seedance-2-0-standard", "seedance_2_0_standard"].includes(normalized)) return "videos-standard";
  if (["videos-fast", "tvideos-fast", "seedance-2.0-fast", "seedance-2-fast", "seedance-2-0-fast", "seedance_2_0_fast"].includes(normalized)) return "videos-fast";
  if (["videos-mini", "videos", "tvideos-mini", "seedance-2.0-mini", "seedance-2-mini", "seedance-2-0-mini", "seedance_2_0_mini"].includes(normalized)) return "videos-mini";
  if (["seedance-2-5", "videos-2.5", "videos-25", "seedance2.5", "seedance25", "seedance2-5", "seedance2_5", "seedance-25", "seedance-2.5", "seedance-2_5", "seedance_25", "seedance_2.5", "seedance_2_5", "seedance_2-5", "videos-2-5", "videos-2_5", "videos_2_5", "videos-seedance-2.5", "videos-seedance2.5"].includes(normalized)) return "seedance-2-5";
  if (["videos-max", "seedance-2.0-max", "seedance-2-max", "seedance_2_0_max"].includes(normalized)) return "videos-max";
  if (["videos-8", "videos-8s", "videos-unlimited", "seedance-2.0-unlimited", "seedance-2-unlimited", "seedance_2_0_unlimited", "seedance_2_unlimited"].includes(normalized)) return "videos-8";
  return "";
}

function modelSummary(values?: string[]): string {
  const selected = normalizeSupportedModels(values);
  return MODEL_OPTIONS.filter((option) => selected.includes(option.value)).map((option) => option.label).join(" / ") || "等待识别";
}

function accountModelPolicy(account: Account): string {
  const selected = normalizeSupportedModels(account.supported_models);
  if (selected.length === 1 && selected[0] === "videos-max") return "Free · 仅 Max";
  if (CREDIT_MODELS.every((model) => selected.includes(model)) && selected.length === CREDIT_MODELS.length) return "会员积分 · 四模型";
  return selected.length ? "自动识别" : "等待识别";
}

function isEnabled(account: Account): boolean {
  if (account.is_active !== undefined) return account.is_active;
  return String(account.status || "").toUpperCase() !== "DISABLED";
}

function isCapacityEligible(account: Account): boolean {
  return isEnabled(account) && String(account.status || "ACTIVE").toUpperCase() === "ACTIVE";
}

function isFuture(value?: string | number | null): boolean {
  if (value === null || value === undefined || value === "") return false;
  const timestamp = typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function cookieSessionValue(cookies: NormalizedCookie[]): string {
  const session = cookies.find((cookie) => cookie.name.toLowerCase() === "__client");
  if (!session?.value) throw new Error("Cookie JSON 中没有有效的 __client 会话");
  return session.value;
}

function readStoredBatchJobIds(): string[] {
  try {
    const value = JSON.parse(sessionStorage.getItem(LOGIN_BATCH_STORAGE_KEY) || "[]") as unknown;
    if (!Array.isArray(value)) return [];
    return [...new Set(value.map(String).filter(Boolean))];
  } catch {
    sessionStorage.removeItem(LOGIN_BATCH_STORAGE_KEY);
    return [];
  }
}

function mergeBatchItems(current: AccountLoginBatchItem[], incoming: AccountLoginBatchItem[]): AccountLoginBatchItem[] {
  const merged = new Map(current.map((item) => [String(item.job.id), item]));
  for (const item of incoming) {
    const id = String(item.job.id);
    const existing = merged.get(id);
    const incomingEmail = item.email || item.job.email || "";
    const displayIdentity = incomingEmail.endsWith("@session.invalid")
      ? existing?.email || incomingEmail
      : incomingEmail || existing?.email || "";
    merged.set(id, {
      email: displayIdentity,
      job: { ...existing?.job, ...item.job },
      ...(item.account || existing?.account ? { account: item.account || existing?.account } : {}),
    });
  }
  return [...merged.values()];
}

function batchStatusCounts(items: AccountLoginBatchItem[]) {
  const result = { active: 0, succeeded: 0, challenge: 0, failed: 0, cancelled: 0 };
  for (const { job } of items) {
    if (job.status === "SUCCEEDED") result.succeeded += 1;
    else if (job.status === "CHALLENGE_REQUIRED") result.challenge += 1;
    else if (job.status === "FAILED" || job.status === "EXPIRED") result.failed += 1;
    else if (job.status === "CANCELLED") result.cancelled += 1;
    else result.active += 1;
  }
  return result;
}

function accountPlanName(account: Account): string {
  const effectiveUnlimited = Boolean(
    account.effective_unlimited
    || account.billing_mode === "unlimited"
    || account.billing_mode === "unlim"
  );
  const appendUnlimited = (value: string) => (
    effectiveUnlimited && !value.toLowerCase().includes("unlimited") && !value.includes("无限")
      ? `${value} · 无限套餐`
      : value
  );
  if (account.plan_name) return appendUnlimited(String(account.plan_name));
  if (typeof account.plan === "string" && account.plan.trim()) return appendUnlimited(account.plan);
  if (account.plan && typeof account.plan === "object") {
    const name = account.plan.type || account.plan.name;
    if (name) return appendUnlimited(String(name));
  }
  const metadataPlan = account.metadata?.plan;
  if (typeof metadataPlan === "string" && metadataPlan.trim()) return appendUnlimited(metadataPlan);
  if (metadataPlan && typeof metadataPlan === "object") {
    const value = metadataPlan as Record<string, unknown>;
    if (value.type || value.name) return appendUnlimited(String(value.type || value.name));
  }
  return effectiveUnlimited ? "无限套餐" : "未识别套餐";
}

function accountEntitlementSummary(account: Account): string {
  if (account.entitlement) return truncate(String(account.entitlement), 32);
  const values = account.entitlements ?? account.metadata?.entitlements;
  if (Array.isArray(values)) {
    const summary = values.map(String).filter(Boolean).slice(0, 2).join(" / ");
    return account.effective_unlimited && !summary ? "无限模式" : summary;
  }
  if (values && typeof values === "object") {
    const summary = Object.entries(values)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([name]) => name)
      .slice(0, 2)
      .join(" / ");
    return account.effective_unlimited && !summary ? "无限模式" : summary;
  }
  return account.effective_unlimited ? "无限模式" : "";
}
